/**
 * One request per resource at a time: callers asking for the same thing while it is in flight share its promise
 * instead of each sending their own. A page mounts several widgets that read the same thing (the sandbox overview,
 * the background tasks, "Live now", the inbox): while a sandbox woke, each of them sent its own request and they all
 * waited together (about 5 overviews and 7 /active calls in parallel). The answer is shared only while it is in flight:
 * a later call asks again (no caching). Callers must not mutate what they get.
 */
const inflight = new Map();

/** `load()` once for every caller of `key` while its promise is pending; the promise resolves or rejects for all. */
export function shareInflight(key, load) {
  const pending = inflight.get(key);
  if (pending) { return pending; }
  let promise;
  try {
    promise = Promise.resolve(load());
  } catch (error) {
    promise = Promise.reject(error);
  }
  const shared = promise.finally(() => {
    if (inflight.get(key) === shared) { inflight.delete(key); }
  });
  inflight.set(key, shared);
  return shared;
}

/** How many shared requests are in flight (tests). */
export function inflightCount() {
  return inflight.size;
}
